import type { FC } from "react";
import styled from "styled-components";
import { Content } from "src/shared/components/content";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { useAppContext } from "src/shared/context/AppContext";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { ImageSvg } from "src/website-apps/shared/components/image-svg";
import { SapphireLearnMoreButton } from "./SapphireLearnMoreButton";

const SAPPHIRE_BLUE = "#002957";

const PRIMARY_COPY_CONTENT_ID = "cid$#3907007#";
const SECONDARY_COPY_CONTENT_ID = "cid$#3907011#";
const BUTTON_CONTENT_ID = "cid$#3907006#";

const Container = styled.div<{ $isCompact?: boolean }>`
  display: flex;
  flex-wrap: nowrap;
  padding: ${({ theme, $isCompact }) => ($isCompact ? theme.spacing(3) : theme.spacing(6))};
  gap: ${({ theme, $isCompact }) => ($isCompact ? theme.spacing(2) : theme.spacing(4.5))};
  background-color: ${SAPPHIRE_BLUE};
  border-radius: ${({ theme }) => theme.borderRadius.l};
  flex-direction: ${({ $isCompact }) => ($isCompact ? "row" : "column")};
  align-items: center;
  @media only screen and (min-width: ${({ theme }) => theme.media.breakpoints.md}px) {
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: ${({ theme }) => theme.spacing(9)};
  }
`;

const CopyContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  gap: 3px; // theme's spacing utility operates on multiples of 4px, '3px' > theme.spacing(0.75)
  text-align: center;

  & > p:first-child {
    color: white;
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
  }

  & > p:last-child {
    color: #e5e9eb; // not in [colors.ts](https://dev.azure.com/viagogo/Packages/_git/Viagogo.Npm.Core?path=/src/themes/shared/colors.ts)
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
  }

  @media only screen and (min-width: ${({ theme }) => theme.media.breakpoints.md}px) {
    text-align: unset;
    & > p:first-child {
      font-size: 16px;
      font-weight: 700;
      line-height: 24px;
    }

    & > p:last-child {
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
    }
  }
`;

interface Props {
  isCompact?: boolean;
  learnMoreUrl: string | undefined;
}

export const SapphireRewardsBanner: FC<Props> = ({ learnMoreUrl, isCompact }) => {
  const appContext = useAppContext();
  const storePageEvent = useStorePageEvent();

  const SAPPHIRE_LOGO = `${appContext.env["REACT_APP_ASSETS_URL"]}/img/chase-sapphire/Chase_Sapphire_Reserve_Logo_RGB_White.svg`;

  return learnMoreUrl ? (
    <Container $isCompact={!!isCompact}>
      <ImageSvg size={{ width: 113, height: 34 }} src={SAPPHIRE_LOGO} alt="Sapphire Rewards Logo" />
      <CopyContainer>
        <p>
          <Content id={PRIMARY_COPY_CONTENT_ID} />
        </p>
        <p>
          <Content id={SECONDARY_COPY_CONTENT_ID} />
        </p>
      </CopyContainer>
      <SapphireLearnMoreButton
        href={learnMoreUrl}
        onClick={() => {
          storePageEvent(PE_TYPE.CLICK, PE_KEYS.SAPPHIRE_REWARDS_LEARN_MORE, appContext.anonymousId);
        }}
      >
        <Content id={BUTTON_CONTENT_ID} />
      </SapphireLearnMoreButton>
    </Container>
  ) : null;
};
